// 도메인 텍스트(label/문구) SSOT.
//
// 네이밍 컨벤션: [CATEGORY]_[NAME]_[PURPOSE(optional)]
//   - CATEGORY: 도메인/영역 prefix (AUTH, CONSENT, PLANT 등)
//   - NAME: 텍스트의 의미를 단독으로 식별 가능한 키워드. 최대한 자기설명적으로.
//   - PURPOSE: NAME만으로 모호할 때만 보강. 무리해서 채우지 않는다 (선택).
//
// ⚠️ **같은 문구 = 같은 키.** 값이 동일한 키를 화면별로 복제하지 않는다(문구 수정 시 드리프트).
// 여러 화면이 공유하는 문구는 아래 3개 횡단 카테고리 중 하나에 두고 재사용한다:
//   - TERM_*   도메인 어휘(지표·필드명). 예: 발전량 · 발전시간 · 인버터 · 설비용량
//   - STATUS_* 상태 어휘(발전소·인버터·알림·구독). 예: 심각 · 주의 · 통신끊김 · 해소
//   - COMMON_* 범용 UI 문구·액션. 예: 다시 시도 · 더보기 · 변경 · 자세히
// 화면 고유 문구만 도메인 prefix(GEN_/REVENUE_/DASHBOARD_ 등)를 쓴다.
//
// flat 구조 이유: 단기 i18n 미계획 상태에서 SSOT/감사 가시성 우선.
// 미래 i18n(lokalise + i18next) 전환 시 스크립트로 dot-namespace 키
// (예: AUTH_LOGIN_TITLE → auth.login.title)로 일괄 변환을 가정한다.
// PURPOSE가 비어있는 키는 변환 스크립트가 placeholder로 채운다.
// ⚠️ i18n 전환 시 주의 — 한국어가 같아 통합된 키 중 번역어가 갈리는 것(예: COMMON_MORE 는
// '더보기' 하나지만 영어로는 load-more / more-actions)은 그 시점에 맥락별로 재분리해야 한다.
import { formatInteger, SUFFIX } from '@heviton/formatter';
import type { SubscriptionStatus } from '@/shared/data-types';

export const UI_TEXT = {
  // brand
  BRAND_NAME: 'HEVITON',

  // ─────────────────────────────────────────────────────────────────────────
  // term (도메인 어휘) — 지표·필드명. 화면(대시보드·발전량·수익·인버터·발전소정보)을 가로지른다.
  // ─────────────────────────────────────────────────────────────────────────
  // 발전 지표
  TERM_GENERATION: '발전량',
  TERM_GENERATION_TOTAL: '총 발전량', // 전체 발전소 합산 스코프
  TERM_GENERATION_HOURS: '발전시간',
  TERM_GENERATION_HOURS_AVG: '평균 발전시간', // 전체 스코프(등가발전시간 평균)
  TERM_GENERATION_HOURS_DAILY_AVG: '일평균 발전시간', // month/year/range 스코프
  TERM_REVENUE: '추정수익', // 발전사업(판매)
  TERM_REVENUE_TOTAL: '총 추정수익', // 전체 발전소 합산 스코프
  TERM_BENEFIT: '요금편익', // 자가소비(절감) — 판매가 아니라 어휘가 다르다
  TERM_CAPACITY: '설비용량',
  TERM_INVERTER: '인버터',
  TERM_INVERTER_CONFIG: '인버터 구성',
  // 기간 단위(PeriodField 조회 단위 · 표 헤더 공용)
  TERM_DAY: '일',
  TERM_MONTH: '월',
  TERM_YEAR: '연',
  TERM_PERIOD: '기간',
  // 날짜·시각
  TERM_DATE: '날짜',
  TERM_TIME: '시간',
  TERM_TODAY: '오늘',
  TERM_START_DATE: '시작일',
  TERM_END_DATE: '종료일',
  // 발전소·계약 필드
  TERM_PLANT_NAME: '발전소 이름',
  TERM_PLANT_USE: '발전용도',
  TERM_CONTRACT_METHOD: '계약방식',
  TERM_FIXED_PRICE: '고정가격',
  TERM_FIXED_UNIT_PRICE: '고정단가',
  TERM_REC_RATE: 'REC가중치',
  // 계정 필드
  TERM_LOGIN_ID: '아이디',
  TERM_PASSWORD: '비밀번호',
  TERM_PHONE: '휴대폰번호',

  // ─────────────────────────────────────────────────────────────────────────
  // status (상태 어휘) — 발전소·인버터·알림등급·구독. 배지·필터·배너 공용.
  // ─────────────────────────────────────────────────────────────────────────
  STATUS_NORMAL: '정상',
  STATUS_CRITICAL: '심각',
  STATUS_WARNING: '주의',
  STATUS_COMM_LOST: '통신끊김',
  STATUS_PREPARING: '운영준비중',
  STATUS_RESOLVED: '해소',
  STATUS_UNRESOLVED: '미해소',
  STATUS_NOT_SUBSCRIBED: '미구독',
  // 구독 상태 — BE `SUBS_STATUS` 공통코드에 대응하되 **문구는 우리가 소유**한다(BE 마스터는 '사용 중'·'중단'·
  // '만료'·'취소'). 상태 표기는 UX 카피라 서비스 톤에 맞춰 통일. 매핑은 `SUBSCRIPTION_STATUS_TEXT`.
  STATUS_SUBSCRIBED: '구독중',
  STATUS_SUSPENDED: '중지됨',
  STATUS_EXPIRED: '구독만료',
  STATUS_CANCELED: '구독취소',

  STATUS_SUBSCRIPTION_ENDED: '구독종료', // 만료·해지 통합(재구독 액션 동일)

  // ─────────────────────────────────────────────────────────────────────────
  // common (범용 UI — a11y 라벨·표준 액션·공용 컨트롤 문구)
  // ─────────────────────────────────────────────────────────────────────────
  COMMON_CLOSE: '닫기',
  COMMON_CANCEL: '취소',
  COMMON_SAVE: '저장',
  COMMON_CONFIRM: '확인',
  COMMON_CHANGE: '변경',
  COMMON_DETAIL: '자세히',
  COMMON_RETRY: '다시 시도',
  COMMON_MORE: '더보기', // 목록 더 불러오기 · 액션 메뉴 트리거 공용
  COMMON_PREVIOUS: '이전',
  COMMON_NEXT: '다음',
  COMMON_SELECT: '선택', // 약관 '선택'(필수 대비) · 셀렉트 드로워 헤더 폴백 공용
  COMMON_SELECT_PLACEHOLDER: '선택...',
  COMMON_SELECT_ALL: '전체 선택',
  COMMON_DESELECT_ALL: '전체 해제',
  COMMON_NO_ITEMS: '항목이 없습니다.',
  COMMON_NO_RESULTS: '조회 결과가 없습니다.',
  COMMON_HOME_CTA: '홈으로 가기',
  COMMON_APP_LINK: '헤빛온 앱 바로가기',
  COMMON_PASSWORD_REQUIRED: '비밀번호를 입력해주세요.',
  // 값 없음 placeholder 단일 소스 — InfoRow 등 빈값 폴백. '-' 직접 하드코딩 금지, 이 상수 경유.
  COMMON_EMPTY_VALUE: '-',
  // 발전소 없음 — 발전소-스코프 페이지 공용 캐논(NoPlantsState). 페이지별 중복 텍스트 흡수.
  COMMON_NO_PLANTS_TITLE: '현재 발전소가 없습니다',
  COMMON_NO_PLANTS_DESC: '접근 권한이 있는 발전소가 없어 조회할 수 없어요.',
  DATE_PLACEHOLDER: '날짜 선택', // DateField 미선택 시 트리거 기본 placeholder
  DATE_MONTH_JUMP_TITLE: '월 선택', // DateField 캡션 → 월 점프 표면(compact 드로워) 헤더
  DATE_PREV_MONTH: '이전 달', // DateField 자체 캡션 행의 월 네비
  DATE_NEXT_MONTH: '다음 달',
  // 네트워크 — 오프라인 전환 시 1회 토스트(app/runtime/network-sync). 브라우저·WebView 공통.
  NETWORK_OFFLINE: '인터넷 연결이 끊겼어요. 연결 상태를 확인해주세요.',

  // app update — 웹에서 새 버전 배포 감지 시 새로고침 안내(widgets/version-update-gate). 닫아도 새로고침.
  APP_UPDATE_TITLE: '새로운 버전이 있어요',
  APP_UPDATE_DESC: '최신 버전으로 업데이트하려면 새로고침해주세요.',
  APP_UPDATE_REFRESH: '새로고침',
  // 강제 업데이트 전용 화면(pages/update-required, native). 되돌아갈 길이 없는 화면이라 카피가 단정적이다.
  UPDATE_REQUIRED_TITLE: '업데이트가 필요합니다',
  UPDATE_REQUIRED_DESC:
    '지금 버전은 더 이상 지원되지 않아요.\n최신 버전으로 업데이트한 뒤 이용해주세요.',
  UPDATE_REQUIRED_CTA: '업데이트하러 가기',

  // contact (채널톡 문의 — 로그인·발전소 목록·결제 안내 등 여러 화면 공통)
  CONTACT_SUPPORT_BUTTON: '채널톡 문의하기',

  // auth
  AUTH_LOGIN_CONTACT_PROMPT: '혹시 로그인 정보를 잊으셨나요? 채널톡으로 문의해주세요!',
  AUTH_LOGIN_TITLE: '헤빛온 계정으로 로그인해주세요.',
  AUTH_LOGIN_TITLE_SUBSCRIPTION: '헤빛온 계정으로 로그인하신 후 구독 결제를 진행해주세요.',
  AUTH_LOGIN_SUBMIT: '로그인',
  AUTH_LOGIN_SUBMITTING: '로그인 중...',
  AUTH_LOGIN_FAILED: '로그인에 실패했습니다.',
  AUTH_LOGIN_SESSION_EXPIRED: '로그인 세션이 만료되었습니다. \n다시 로그인해주세요.',
  AUTH_LOGIN_ID_REQUIRED: '아이디를 입력해주세요.',
  AUTH_LOGOUT: '로그아웃',
  AUTH_TEMPPASSWORD_NOTICE:
    '임시 비밀번호를 변경해주세요.\n비밀번호는 [정보관리] → [마이페이지] 에서 언제든 변경할 수 있습니다.',
  AUTH_TEMPPASSWORD_SKIP: '다음에 할게요.',

  // consent (약관 동의 온보딩 — `/auth/consent`). '보기'는 고객센터 약관 목록과 동일한 원문 뷰어(openConsent).
  CONSENT_AGREEMENT_TITLE: '서비스 약관 동의',
  CONSENT_AGREEMENT_DESCRIPTION: '서비스 이용을 위해서는 동의가 필요합니다.',
  CONSENT_AGREEMENT_ALL: '전체 동의',
  CONSENT_AGREEMENT_REQUIRED: '필수',
  CONSENT_AGREEMENT_VIEW: '보기',
  CONSENT_AGREEMENT_SUBMIT: '동의 완료',
  CONSENT_AGREEMENT_SUBMITTING: '처리 중...',
  CONSENT_AGREEMENT_FAILED: '동의 처리에 실패했습니다.',

  // error
  ERROR_BOUNDARY_TITLE: '문제가 발생했습니다',
  ERROR_BOUNDARY_DESCRIPTION:
    '예기치 못한 오류로 화면을 표시할 수 없습니다. 잠시 후 다시 시도해주세요.',
  ERROR_NOTFOUND_MESSAGE: '페이지를 찾을 수 없습니다.',
  ERROR_NOTFOUND_DESCRIPTION: '요청하신 페이지가 존재하지 않거나 이동되었습니다.',
  ERROR_CRITICAL_TITLE: '데이터를 불러오지 못했습니다',
  ERROR_CRITICAL_DESCRIPTION:
    '일시적인 오류로 정보를 표시할 수 없습니다. 잠시 후 다시 시도해주세요.',
  ERROR_MAINTENANCE_TITLE: '서비스 점검 중입니다',
  ERROR_MAINTENANCE_DESCRIPTION:
    '서비스 점검으로 일시적으로 이용할 수 없습니다. 점검 완료 후 다시 시도해주세요.',
  // 글로벌 401 — (1) 진입 토큰 실패(`?reason=link_expired`), (2) 세션 중 401(`session_expired`),
  // (3) 그 외 미인증(`unauthenticated`) 을 한 화면으로 흡수. 카피만 사유 쿼리로 분기.
  ERROR_UNAUTHORIZED_TITLE: '로그인이 필요합니다',
  ERROR_UNAUTHORIZED_DESCRIPTION:
    '인증 정보가 만료되었거나 유효하지 않습니다. 다시 로그인해주세요.',
  ERROR_UNAUTHORIZED_DESCRIPTION_LINK_EXPIRED: '접속 링크가 만료되었습니다. 다시 로그인해주세요.',
  ERROR_UNAUTHORIZED_DESCRIPTION_SESSION_EXPIRED:
    '로그인 세션이 만료되었습니다. 다시 로그인해주세요.',
  ERROR_UNAUTHORIZED_LOGIN_CTA: '로그인하기',

  // subscription (구독 phase 진입 = 발전소 목록)
  SUBSCRIPTION_LIST_GREETING_TITLE: '님, 안녕하세요!',
  SUBSCRIPTION_LIST_GREETING_SUBSCRIBE: '구독을 원하는 발전소를 선택해주세요.',
  SUBSCRIPTION_LIST_EMPTY: '발전소가 없습니다.',
  SUBSCRIPTION_LIST_EMPTY_DESC: '관련 문의는 채널톡을 이용해주세요!',
  SUBSCRIPTION_SUBSCRIBE: '구독하기', // 목록 CTA · 발전소 상세 구독정보 공용
  SUBSCRIPTION_LIST_CTA_RESUBSCRIBE: '재구독하기',
  SUBSCRIPTION_LIST_CTA_CHANGE_PAYMENT: '결제 변경',
  // 비대표 — 과금 액션(구독/재구독/결제변경) 버튼을 이 라벨의 **비활성 버튼**으로 대체. 버튼을 그냥 지우면
  // "왜 안 보이지"가 되므로 자리와 사유를 남긴다. 액션·화면 중립 문구(구독목록·마이페이지·정보상세 공용).
  SUBSCRIPTION_NEEDS_PAYMENT_ROLE: '결제 권한 필요',
  SUBSCRIPTION_LIST_LOAD_MORE_RETRY: '불러오지 못했어요. 다시 시도',
  // 하단 바로가기 (헤빛온 앱 / REMS 웹 홈) + 결제수단 관리 + 채널톡 안내
  SUBSCRIPTION_LIST_MANAGE_PAYMENT: '결제수단 관리하기',
  SUBSCRIPTION_LIST_SUPPORT_PROMPT: '구독 관련 문의는 채널톡을 이용해주세요!',
  // 구독유형·구독상태 라벨은 BE 공통코드(SUBS/SUBS_STATUS)가 SSOT — `entities/common-code` 의 `useCommonCode` 사용.

  // brand footer (HERIT CORP. 정보) — BrandLayout 의 푸터에서 사용. login/subscriptions 공통.
  // 약관·개인정보처리방침 링크 문구는 AGREEMENT_*_TITLE 재사용.
  BRAND_FOOTER_COMPANY_NAME: 'HERIT CORP.',
  BRAND_FOOTER_COMPANY_INFO:
    '(주)헤리트 | 대표이사: 한미숙 | 경기도 성남시 분당구 대왕판교로 660, A동 1008호 | 사업자번호: 314-81-30305 | 통신판매업신고: 제2019-성남분당 B-0475호 | 대표번호: 031-606-7150',

  // agreement (약관 — 이용약관·개인정보처리방침·환불정책). 제목은 푸터 링크·목록 공용.
  AGREEMENT_TERMS_TITLE: '이용약관',
  AGREEMENT_PRIVACY_TITLE: '개인정보처리방침',
  AGREEMENT_REFUND_TITLE: '환불 정책',
  AGREEMENT_ERROR: '약관을 불러오지 못했습니다.', // 온보딩 동의·고객센터 목록·원문 뷰어 공용

  // support (고객센터 `/support` — 컨텐츠 합성: 문의·동의항목·앱버전)
  SUPPORT_CONTACT_PROMPT: '운영 중 궁금한 점은 채널톡으로 문의해 주세요.',

  // payment-method (결제수단 변경 팝업 — 구독중 카드 [결제 변경])
  PAYMENT_METHOD_TITLE: '결제수단',
  PAYMENT_METHOD_MANAGE_TITLE: '결제수단 관리',
  PAYMENT_METHOD_REGISTERED: '등록된 결제수단',
  PAYMENT_METHOD_EMPTY: '등록된 결제수단이 없습니다.',
  PAYMENT_METHOD_DELETE: '삭제',
  PAYMENT_METHOD_DELETE_CONFIRM_TITLE: '결제수단 삭제',
  PAYMENT_METHOD_DELETE_CONFIRM_MESSAGE:
    '결제수단을 삭제하시면 다시 복구할 수 없습니다. 아래 결제수단을 삭제하시겠습니까?',
  PAYMENT_METHOD_DELETE_ERROR: '결제수단 삭제에 실패했습니다. 잠시 후 다시 시도해주세요.',
  PAYMENT_METHOD_NEW: '새로운 결제수단',
  PAYMENT_METHOD_CURRENT: '현재 이용중',
  PAYMENT_METHOD_NEW_CARD: '카드 자동결제',
  PAYMENT_METHOD_NEW_TRANSFER: '계좌 자동이체',
  PAYMENT_METHOD_TRANSFER_BUSINESS_WARNING: '법인 계좌는 지원되지 않습니다.',
  PAYMENT_METHOD_REGISTER_CTA: '등록',
  PAYMENT_METHOD_REGISTER_ERROR: '결제수단 등록을 시작하지 못했습니다.',
  PAYMENT_METHOD_CHANGE_CTA: '변경하기',
  PAYMENT_METHOD_CONFIRM_TITLE: '결제수단 변경 확인',
  PAYMENT_METHOD_CONFIRM_MESSAGE: '결제수단을 아래 정보로 변경하시겠습니까?',
  // 결제 실행 CTA — 구독유형 선택(plan) 제출 + 결제수단 다이얼로그 subscribe intent 공용.
  PAYMENT_SUBMIT: '결제하기',
  PAYMENT_METHOD_SUBSCRIBE_CONFIRM_TITLE: '구독 확인',
  PAYMENT_METHOD_SUBSCRIBE_CONFIRM_MESSAGE: '이 결제수단으로 구독하시겠습니까?',
  PAYMENT_METHOD_SUBSCRIBE_ERROR: '결제에 실패했습니다. 잠시 후 다시 시도해주세요.',
  PAYMENT_METHOD_CHANGE_DONE: '결제수단이 변경되었습니다.',
  PAYMENT_METHOD_CHANGE_ERROR: '결제수단 변경에 실패했습니다. 잠시 후 다시 시도해주세요.',
  PAYMENT_METHOD_ERROR: '결제수단을 불러오지 못했습니다.',
  // 카드 종류 라벨 (BE cardType) — ※ 표기 미확정(payment-method-decisions §)
  PAYMENT_METHOD_CARD_CREDIT: '신용카드',
  PAYMENT_METHOD_CARD_CHECK: '체크카드',
  PAYMENT_METHOD_CARD_GIFT: '기프트',
  PAYMENT_METHOD_TRANSFER_LABEL: '계좌이체',
  // 결제수단 상태 (BE billingStatus, active 외) — 선택 불가 사유 표기
  PAYMENT_METHOD_STATUS_EXPIRED: '만료',
  PAYMENT_METHOD_STATUS_REVOKED: '해지',

  // plan (구독유형 선택 페이지)
  PLAN_TITLE_SUFFIX: '의 구독유형을 선택해주세요.',
  PLAN_PRICE_DISCOUNT: '할인', // `(14% 할인)`
  PLAN_PRICE_PER_MONTH: '/월', // `15,000원/월` — 금액(원 포함) 뒤에 붙임
  PLAN_PRICE_MONTHS: '개월', // `x 60개월`, `59개월간`
  PLAN_PRICE_RTU: 'RTU',
  PLAN_PRICE_EXTRA_FEE: '추가요금',
  PLAN_PRICE_COUNT: '개',
  PLAN_PRICE_VAT: 'VAT', // 요금 내역 꼬리 — 내역 금액이 공급가라 VAT 가 별도로 붙는다는 표기
  PLAN_TODAY_PAYMENT: '오늘 결제하실 금액',
  PLAN_VAT_INCLUDED: 'VAT 포함',
  PLAN_MONTHLY_RECURRING_AFTER: '이후',
  PLAN_MONTHLY_RECURRING_EACH: '매달 결제',
  PLAN_NOTICE_PENALTY:
    '계약 중도 해지 시 남은 기간 요금의 10%에 해당하는 해지위약금과 잔여기간에 비례한 설치지원비 반환금이 발생합니다.',
  PLAN_NOTICE_RTU_RETURN: '계약 종료 또는 해지 시 모니터링 장치(RTU) 반납이 필요합니다.',
  PLAN_AGREE_REFUND: '[필수] 위약금 및 환불규정을 확인했습니다.',
  PLAN_VIEW_FULL: '전문보기',
  PLAN_PLANT_NOT_FOUND: '선택한 발전소를 찾을 수 없습니다.',
  PLAN_NO_PRODUCTS: '이용 가능한 구독 상품이 없습니다.',

  // toss (카드 등록 인증창 콜백 페이지 — /subscriptions/callback/:result).
  TOSS_CALLBACK_PROCESSING: '결제수단을 등록하고 있습니다...',
  TOSS_CALLBACK_FAIL: '결제수단 등록에 실패했습니다. 잠시 후 다시 시도해주세요.',

  // result (결제 결과 화면 — /subscriptions/result). 제목/보조문은 producer 가 state 로 주입.
  RESULT_SUCCESS_TITLE: '결제가 완료됐어요!',
  RESULT_SUCCESS_SUBTITLE: '감사합니다.',
  RESULT_REGISTER_TITLE: '결제수단이 등록됐어요!',
  RESULT_REGISTER_SUBTITLE: '돌아가서 결제를 이어서 진행해주세요.',
  RESULT_REGISTER_BACK: '돌아가기',
  // 일시납 등 상담 결제 안내(plan 에서 recurring 아닌 상품 선택 시).
  // 결제 비활성(env `tossPaymentEnabled=false`) — 구독 퍼널 전체가 이 안내로 돌아간다(pending = 채널톡 문의 + 뒤로가기).
  RESULT_PAYMENT_CLOSED_TITLE: '구독 문의하기',
  RESULT_PAYMENT_CLOSED_SUBTITLE: '구독 신청·결제는 채널톡으로 문의해주세요.',
  RESULT_LUMPSUM_TITLE: '일시납 결제 안내',
  RESULT_LUMPSUM_SUBTITLE:
    '선택하신 상품은 담당자 상담 후 결제가 가능합니다. 채널톡으로 문의주시면 친절하게 설명해 드리겠습니다.',
  RESULT_FAIL_TITLE: '결제정보 등록 중 문제가 생겼어요.',
  // 토스 에러 메시지가 없을 때만 쓰는 폴백(보통은 토스 message + (code) 를 표시).
  RESULT_FAIL_SUBTITLE: '결제정보를 정확하게 등록해주세요.',
  RESULT_OTHER_PLANT: '다른 발전소 구독하기',
  RESULT_BACK: '뒤로가기',

  // alert (발전소 이상 알림 — 사용자 대면. 데이터 entity 는 event). 화면 타이틀은 라우트 handle(NAV_ALERTS) 소유.
  // 알림등급·해소 여부 라벨은 STATUS_*(공용 상태 어휘) 재사용.
  ALERT_CARD_FIRST_OCCURRED: '최초 발생',
  ALERT_CARD_LAST_OCCURRED: '최근 발생',
  ALERT_CARD_RESOLVED_AT: '알림 해소',
  ALERT_CARD_TIP: 'TIP', // 메시지(해결 안내) 배지 라벨
  ALERT_CARD_TIP_LABEL: '해결 안내', // TIP 메시지 스크린리더 컨텍스트(sr-only)
  ALERT_LIST_EMPTY: '알림이 없습니다.',
  ALERT_LIST_EMPTY_DESC: '선택한 조건의 발전소 이상 알림이 없습니다.',
  // alert 필터(조회조건) — 조회조건/칩/적용/초기화 라벨은 공용 `FILTER_*`(FilterShell) 사용.
  ALERT_FILTER_PLANT: '발전소',
  ALERT_FILTER_GRADE: '알림등급',
  ALERT_FILTER_PERIOD: '조회기간',
  ALERT_FILTER_PERIOD_7: '최근 7일',
  ALERT_FILTER_PERIOD_30: '최근 30일',
  ALERT_FILTER_PERIOD_90: '최근 90일',
  ALERT_FILTER_PERIOD_CUSTOM: '직접 선택',
  ALERT_FILTER_SORT: '정렬',
  ALERT_FILTER_STATUS: '알림 상태',
  ALERT_FILTER_STATUS_RESOLVED_CHIP: '해소 포함', // 해소 포함 시 칩 라벨(제거=미포함 복귀)
  ALERT_FILTER_SORT_LATEST: '최신순',
  ALERT_FILTER_SORT_OLDEST: '오래된순',
  ALERT_FILTER_DEVICE_HINT: '발전소를 먼저 선택해주세요.',
  ALERT_FILTER_INVALID_RANGE: '시작일이 종료일보다 늦을 수 없어요.',
  ALERT_FILTER_MAX_RANGE: '조회기간은 최대 90일까지 선택할 수 있어요.',
  ALERT_FILTER_GRADE_REQUIRED: '알림등급을 1개 이상 선택해주세요.',

  // filter (공용 반응형 조회 쉘 `FilterShell` — Drawer: non-pc bottom / pc right)
  FILTER_TITLE: '조회조건', // 트리거 버튼 + 드로워 제목 공용
  FILTER_APPLY: '조회',
  FILTER_RESET: '초기화',
  FILTER_ALL: '전체', // 필터 칩 '전체'(중립) 상태 공통 텍스트 — `차원: 전체`
  FILTER_ACTIVE: '적용된 필터', // 칩 목록 a11y 라벨
  FILTER_CHIP_REMOVE: (label: string) => `${label} 필터 제거`, // 칩 x a11y 라벨

  // export (표 → 파일 내보내기, `shared/export`). web 빌드 전용 — native 는 버튼 자체가 없다.
  EXPORT_XLSX: '엑셀', // Toolbar 액션 라벨(pc). non-pc 는 아이콘만 + 이 텍스트가 aria-label
  EXPORT_XLSX_EMPTY: '내보낼 데이터가 없어요.', // 조회 결과 0건에서 눌렀을 때(버튼 disabled 사유 툴팁 겸용)
  EXPORT_XLSX_FAILED: '엑셀 다운로드에 실패했어요. 잠시 후 다시 시도해주세요.',
  EXPORT_CONDITIONS: '조회조건', // 내보낸 파일 상단 조회조건 블록 라벨

  // 발전소 선택(컨텍스트 바 → PlantSelectSheet).
  SCOPE_SELECTOR_ARIA: '발전소 선택',
  SCOPE_SELECTOR_CHANGE: '발전소 변경',
  SCOPE_ALL_PLANTS: '전체 발전소', // allowAll 스코프(발전량/수익) — 전 발전소 합산
  SCOPE_ALL_SUMMARY: (count: string, capacity: string) => `총 ${count} / ${capacity}`, // 전체 셀렉터 부제
  SCOPE_SELECT_TITLE: '발전소를 선택하세요',
  SCOPE_SELECT_SEARCH_PLACEHOLDER: '발전소명·주소 검색',
  SCOPE_SELECT_EMPTY: '검색 결과가 없습니다.',
  // 선택 가능 발전소 0개(hasNoSelectable) 빈 상태 — 사유별 문구(D4). CTA(구독하러 가기)는 구독 페이지 이식 후.
  SCOPE_EMPTY_SUBSCRIPTION_TITLE: '구독 중인 발전소가 없습니다',
  SCOPE_EMPTY_SUBSCRIPTION_DESC: '구독 중인 발전소만 조회할 수 있어요.',
  SCOPE_EMPTY_PREPARING_TITLE: '운영준비중 발전소만 있습니다',
  SCOPE_EMPTY_PREPARING_DESC: '운영준비중 발전소는 아직 조회할 데이터가 없어요.',
  SCOPE_EMPTY_MIXED_TITLE: '표시할 수 있는 발전소가 없습니다',
  SCOPE_EMPTY_MIXED_DESC: '조회 가능한 발전소가 없어요.',

  // 목록 footer 결과 건수 — "총 N개 중 M개"(formatInteger). PaginatedDataSection count prop 이 사용.
  RESULT_COUNT: (total: number, filtered: number) =>
    `총 ${formatInteger(total, SUFFIX.COUNT)} 중 ${formatInteger(filtered, SUFFIX.COUNT)}`,
  // 카운트가 목록과 분리된 별도(느린) 쿼리인 목록 — 총계 로딩/실패 시 footer 강등 문구(목록은 그대로).
  RESULT_COUNT_PENDING: '총계 계산 중…',
  RESULT_COUNT_FAILED: '총계 조회 실패',
  MONTH_LABEL: (n: number) => `${n}월`, // MonthField 12개월 그리드
  MONTH_FIELD_PREV_YEAR: '이전 연도', // MonthField 연도 네비
  MONTH_FIELD_NEXT_YEAR: '다음 연도',

  // nav (앱 셸 — 데스크탑 좌측 icon-rail 드로어 / 모바일 bottom sheet 메뉴)
  // 하단 탭 좁은 폭용 축약 라벨은 TERM_GENERATION('발전량') · TERM_INVERTER('인버터') 재사용.
  NAV_MENU: '메뉴',
  // 사이드바 토글 — **누르면 일어날 일**을 상태별로 말한다(툴팁·aria-label 공용).
  // 아이콘 레일로 좁아지는 형태라 '열기/닫기'가 아니라 '펼치기/접기'.
  NAV_EXPAND: '사이드바 펼치기',
  NAV_COLLAPSE: '사이드바 접기',
  NAV_BACK: '뒤로',
  NAV_THEME_TOGGLE: '테마 전환(라이트/다크/시스템)',
  NAV_DASHBOARD: '대시보드',
  NAV_STATS: '발전통계',
  NAV_STATS_GENERATION: '발전량 분석',
  NAV_STATS_INVERTER: '인버터 상세분석', // 메뉴 라벨 겸 화면 타이틀
  NAV_REVENUE: '수익분석',
  NAV_ALERTS: '이상알림',
  NAV_INFO: '정보관리',
  NAV_INFO_MY: '마이페이지',
  NAV_INFO_PLANTS: '발전소정보',
  NAV_SUPPORT: '고객센터',

  // 발전량 분석 (/stats/generation) — 구앱 statistics 화면 이식.
  // 지표·표 헤더 라벨은 TERM_*(발전량·발전시간·날짜·월) 재사용.
  GEN_SERIES_IRRADIANCE: '일사량',
  GEN_TABLE_COL_TIME: '시간대', // day
  GEN_HOUR_LABEL: (hour: number) => `${hour}시 ~ ${hour + 1}시`,
  GEN_YEAR_MONTH_LABEL: (year: string, month: number) => `${year}년 ${month}월`,
  GEN_EMPTY: '조회된 데이터가 없습니다.',
  // 보기 방식(전체 스코프 전용) — 합산=전 발전소 한 계열 / 발전소별=발전소당 계열(라인차트 + 표 컬럼).
  GEN_FILTER_VIEW_MODE: '보기 방식',
  GEN_VIEW_MODE_SUM: '합산',
  // 분리 보기 라벨 — 같은 축이지만 스코프가 분리 단위를 정한다(전체=발전소별 / 단일=인버터별).
  GEN_VIEW_MODE_BY_PLANT: '발전소별',
  GEN_VIEW_MODE_BY_INVERTER: '인버터별',
  /** 차트 카드 결합 트리거 라벨 — 두 축의 현재 값을 한 줄로("합산 발전량"·"발전소별 발전시간"). 대시보드 추이 정합. */
  GEN_TREND_OPTION: (viewMode: string, metric: string) => `${viewMode} ${metric}`,
  // 지표(발전소별 보기 전용) — 값 자체가 바뀌므로 차트·표에 함께 적용(WYSIWYG). 라벨은 TERM_GENERATION(_HOURS) 재사용.
  GEN_FILTER_METRIC: '지표',
  // 발전소별 표 2번째 컬럼 — 발전량은 가로 합, 발전시간은 **평균**(등가발전시간은 용량 정규화 값이라 합이 무의미).
  GEN_TABLE_COL_TOTAL: '합계',
  GEN_TABLE_COL_AVERAGE: '평균',
  /** 표 하단 합계행 라벨 — 전 컬럼 동일 규칙(각 컬럼의 세로 합). */
  GEN_TABLE_FOOTER_TOTAL: '합계',
  // 조회조건 기간 필터(발전량·수익 공용) — admin 조회 룩(날짜기준 select + 단일/기간 필드, live). 날짜기준 select
  // 옵션·조회기준 칩 값은 TERM_DAY/MONTH/YEAR/PERIOD(일/월/연/기간) 재사용.
  PERIOD_FILTER_DATE_TYPE: '날짜기준',
  // 조회 대상 차원명 — 날짜기준별(일=조회일자·월=조회월·연=조회연도·기간=조회기간). 값 필드 라벨 + 조회기간 칩.
  PERIOD_FILTER_DATE_DIM: (period: string): string =>
    period === 'month'
      ? '조회월'
      : period === 'year'
        ? '조회연도'
        : period === 'range'
          ? '조회기간'
          : '조회일자',
  // 수익분석 (/revenue) — 구앱 revenue 화면 이식.
  // 지표·표 헤더는 TERM_*(발전량·총 발전량·추정수익·요금편익·날짜·월) 재사용.
  REVENUE_SERIES_SMP: 'SMP 추정수익',
  REVENUE_SERIES_REC: 'REC 추정수익',
  REVENUE_MASKED: '?,???,???원', // 미등록 발전소 수익 마스킹
  REVENUE_EMPTY: '수익 데이터가 없습니다.',
  // 상세시트(발전사업 단일 행 클릭)
  REVENUE_DETAIL_FIXED_PRICE_DESC: (recRate: string) => `SMP + 1REC x ${recRate}`,
  REVENUE_DETAIL_SMP: 'SMP 수익',
  REVENUE_DETAIL_SMP_DESC: (price: string) => `${price}원/kWh 적용`,
  REVENUE_DETAIL_REC: 'REC 수익',
  REVENUE_DETAIL_REC_DESC: (price: string) => `${price}원/REC 적용`,
  REVENUE_DETAIL_TOTAL: '합계',
  // Footer — 시장가(Market) / 계약(Contract)
  REVENUE_MARKET_SMP: 'SMP 시장가격',
  REVENUE_MARKET_REC: 'REC 현물',
  REVENUE_CONTRACT_TITLE: '한전 계약종별',
  REVENUE_CONTRACT_SELF_NOT_SET: '이용중인 계약종별을 알려주세요.',
  REVENUE_CONTRACT_SELF_SET: '계약종별이 설정됐습니다!',
  REVENUE_CONTRACT_INPUT: '계약종별 입력',
  REVENUE_CONTRACT_PRICE_P101: '고정가격 (SMP+1REC)',
  REVENUE_CONTRACT_PRICE_P103: '고정가격 (SMP+1REC×가중치)',
  // 미등록 안내 팝업(RevenuePopup)
  REVENUE_POPUP_SELF_TITLE: '발전소 요금편익 안내',
  REVENUE_POPUP_SELF_MESSAGE:
    "한전 계약종별 '주택용 저압'을 기준으로 요금편익을 계산합니다. 계약종별을 입력하시면 보다 정확한 분석이 가능합니다.\n계약종별은 전기요금 고지서, 또는 한전 고객센터(국번없이 123)에서 확인할 수 있습니다.",
  REVENUE_POPUP_SALE_TITLE: '발전소 추정수익 안내',
  REVENUE_POPUP_SALE_MESSAGE:
    '발전소 추가 정보를 입력하시면 추정수익을 보다 정확하게 분석해 드립니다.',
  REVENUE_POPUP_SALE_ITEM_SALE: '발전사업용 발전소',
  REVENUE_POPUP_SALE_ITEM_SALE_DESC: ': 계약정보',
  REVENUE_POPUP_SALE_ITEM_SELF: '자가소비용 발전소',
  REVENUE_POPUP_SALE_ITEM_SELF_DESC: ': 한전 전기요금 정보',
  REVENUE_POPUP_CONFIRM: '발전소 추가정보 입력',
  // 용도 미설정(NOT_REGISTERED) 단일 발전소 — 중립 상태(D1-B). 발전사업 오정보(추정수익·Market) 대신 용도설정 유도.
  REVENUE_UNSET_TITLE: '발전소 용도가 설정되지 않았습니다',
  REVENUE_UNSET_DESC: '발전소 용도(발전사업용·자가소비용)를 설정하면 수익을 분석할 수 있어요.',
  REVENUE_UNSET_CTA: '용도 설정하기',

  // 대시보드 (/ = 홈) — 구앱 dashboard 화면(전체/단일) 이식. 오늘 운영현황·상태·수익 모니터링.
  // 메트릭·상태 라벨은 TERM_*/STATUS_* 재사용(발전량·발전시간·추정수익·요금편익 / 정상·심각·주의·통신끊김).
  DASHBOARD_SECTION_PLANT_LIST: '발전소 목록', // 전체 발전소 목록 카드
  // 최근 이상알림 카드(단일) — 인버터 구성 카드와 나란히. 헤더 액션은 이상알림 목록(해당 발전소) 드릴인.
  DASHBOARD_SECTION_RECENT_ALERTS: '최근 이상알림',
  DASHBOARD_RECENT_ALERTS_EMPTY: '최근 이상알림이 없습니다',
  // 발전량 추이 스택 차트 위젯(전체) — 발전소별 스택 막대 + 일/월/연·발전량/발전시간 토글.
  DASHBOARD_GEN_TREND_TITLE: (metric: string) => `${metric} 추이`, // metric=발전량/발전시간
  DASHBOARD_GEN_PERIOD_DAY: '일간',
  DASHBOARD_GEN_PERIOD_MONTH: '월간',
  DASHBOARD_GEN_PERIOD_YEAR: '연간',
  // 기간·타입 결합 트리거 — 두 축을 한 버튼 라벨로("일간 발전량"). Drawer 안에서 축별로 분리 선택.
  DASHBOARD_GEN_TREND_OPTION: (period: string, metric: string) => `${period} ${metric}`,
  DASHBOARD_GEN_GROUP_METRIC: '타입',
  // 발전 추이 차트 위젯(단일) — 발전량 막대 + 발전시간 라인 + 일사량 배경(일간). 기간 토글만.
  DASHBOARD_SINGLE_TREND_TITLE: '발전량 추이',
  // 제목 옆 기준 표기 — 일간은 수신시각까지, 월/연은 조회 기준값(yyyy-MM / yyyy)만.
  DASHBOARD_SINGLE_TREND_RECEIVED: (at: string) => `${at} 수신`,
  DASHBOARD_METRIC_CURRENT_POWER: '현재 출력',
  DASHBOARD_METRIC_ABNORMAL_PLANTS: '이상 발전소', // 전체 4카드 — 탭하면 이상알림으로 이동
  DASHBOARD_METRIC_ABNORMAL_VALUE: (count: number, total: number) =>
    `${formatInteger(count, SUFFIX.COUNT)} / 총 ${formatInteger(total, SUFFIX.COUNT)}`,
  DASHBOARD_METRIC_ABNORMAL_NONE: '없음', // 이상 0개 — '0개 / 총 N개' 대신(파란색)
  DASHBOARD_PLANT_PREPARING_STATUS: '운영준비중..', // 402 행 트레일링(비활성)
  DASHBOARD_SHOW_MORE: (count: string) => `${count} 더보기`, // 6개 이상 접기/펴기
  DASHBOARD_SHOW_LESS: '접기',
  // 발전소 행 하단 지표는 TERM_*(설비용량·오늘·발전량·발전시간) 재사용, 현재출력(%·kW)은 PlantTrailing.
  DASHBOARD_INVERTER_EMPTY: '등록된 인버터가 없습니다', // 단일 인버터 0건
  // 알림 배너(단일) — 전체(ALL) 배너는 '이상 발전소' 카드로 대체돼 문구도 함께 제거.
  DASHBOARD_ALERT_SINGLE_CRITICAL: '발전소에 심각한 문제가 있어요.',
  DASHBOARD_ALERT_SINGLE_DISCONNECTED: '발전소 통신이 원활하지 않아요.',
  DASHBOARD_ALERT_SINGLE_WARNING: '발전소 점검이 필요해요.',
  DASHBOARD_ALERT_SINGLE_OPERATING: '정상 동작하고 있어요.',
  DASHBOARD_ALERT_SINGLE_FINISHED: '오늘 발전을 마쳤어요.',

  // 정보관리 > 발전소 정보 (목록 `/plants` · 상세 `/plants/:plantId`) — 구앱 info 화면 이식.
  // InfoRow 트레일링 액션 라벨(구앱 SectionRow t.actions) — 값 유무로 버튼 표현 분기.
  // '변경'·'자세히'는 COMMON_CHANGE/COMMON_DETAIL 재사용.
  PLANT_INFO_ACTION_EDIT: '수정',
  PLANT_INFO_ACTION_CREATE: '입력',
  PLANT_INFO_NOT_ENTERED: '미입력', // 액션 행 빈 값(입력 유도) — 비-액션 행은 COMMON_EMPTY_VALUE('-')
  // 상세 섹션 제목
  PLANT_INFO_SECTION_BASIC: '기본정보',
  PLANT_INFO_SECTION_INSTALL: '설치정보',
  PLANT_INFO_SECTION_CONTRACT: '계약정보',
  PLANT_INFO_SECTION_KEPCO: '한전 요금정보', // 자가소비(02) 계약 섹션 제목
  // 기본정보 필드 — 이름·설비용량·발전용도·계약방식·고정가격·REC가중치는 TERM_* 재사용.
  PLANT_INFO_FIELD_ENERGY: '에너지원',
  PLANT_INFO_FIELD_LOCATION: '설치위치',
  PLANT_INFO_FIELD_INSTALLED_AT: '설치일자',
  PLANT_INFO_FIELD_BUSINESS: '사업구분',
  PLANT_INFO_INSTALLED_AT: (year: number, month: number, day: number) =>
    `${year}년 ${month}월 ${day}일`,
  // 설치정보 필드
  PLANT_INFO_FIELD_RTU: 'RTU 업체',
  PLANT_INFO_FIELD_CONSTRUCT: '시공업체',
  PLANT_INFO_FIELD_INVERTER_CONFIG: '인버터구성',
  PLANT_INFO_INVERTER_COUNT: (n: number) => `${formatInteger(n, SUFFIX.COUNT)}`,
  // 계약정보 필드
  PLANT_INFO_FIELD_CONTRACT_PERIOD: '계약기간',
  // 발전용도명(useCode 파생) — 01 발전사업 · 02 자가소비.
  PLANT_USE_SALE: '발전사업용',
  PLANT_USE_SELF: '자가소비용',
  // 상세 상태(로드 실패·없음)
  PLANT_INFO_NOT_FOUND: '발전소 정보를 불러올 수 없습니다.',
  // 발전소 이름 수정 시트(구앱 rename BottomSheet) — 필드 라벨은 TERM_PLANT_NAME 재사용.
  PLANT_INFO_RENAME_TITLE: '발전소 이름 수정',
  PLANT_INFO_RENAME_PLACEHOLDER: '발전소 이름을 입력하세요',
  PLANT_INFO_RENAME_REQUIRED: '발전소 이름을 입력하세요.',
  PLANT_INFO_RENAME_MAXLEN: '20자 이내로 입력하세요.',
  PLANT_INFO_RENAME_EMOJI: '이모지는 사용할 수 없습니다.',
  PLANT_INFO_RENAME_SUCCESS: '발전소 이름이 변경되었습니다.',
  PLANT_INFO_RENAME_ERROR: '발전소 이름 변경에 실패했습니다.',
  // 목록(`/plants`) — 미완료(운영준비중·계약 미설정) 상단 분리.
  PLANT_LIST_STATE_NOT_CONFIGURED: '계약정보 미설정',
  PLANT_LIST_STATE_COMPLETE: '설정완료',
  PLANT_LIST_INCOMPLETE_NOTICE: '정보가 미입력된 발전소가 있습니다',
  PLANT_LIST_EMPTY: '발전소 정보가 없습니다.',
  PLANT_LIST_SUBTITLE: (location: string, capacity: string) => `${location} / ${capacity}`,

  // 발전소 용도/계약 설정·변경 (`/plants/:plantId/use`) — 구앱 contract 화면 이식.
  // 발전용도·계약방식·고정가격·REC가중치 라벨은 TERM_* 재사용.
  PLANT_USE_TITLE: '발전소 용도·계약',
  PLANT_USE_PLACEHOLDER_USE: '발전용도를 선택하세요',
  PLANT_USE_FIELD_CONTRACT_SELF: '계약종별', // 자가소비(02)
  PLANT_USE_PLACEHOLDER_CONTRACT_SALE: '계약방식을 선택하세요',
  PLANT_USE_PLACEHOLDER_CONTRACT_SELF: '계약종별을 선택하세요',
  // 계약 상세(발전사업 P101/P103 고정단가 · P102 현물)
  PLANT_USE_SECTION_DETAIL: '계약 상세',
  PLANT_USE_FIELD_PRICE_STANDARD: '기준가격',
  PLANT_USE_PRICE_STANDARD_P101: 'SMP + 1REC',
  PLANT_USE_PRICE_STANDARD_P103: 'SMP + 1REC × 가중치',
  PLANT_USE_FIELD_START_DATE: '계약시작일',
  PLANT_USE_FIELD_END_DATE: '계약종료일',
  PLANT_USE_VALIDATION_REC_RATE: 'REC가중치는 0 ~ 2.5 사이로 입력하세요.',
  PLANT_USE_VALIDATION_END_DATE: '계약 종료일은 시작일 이후여야 합니다.',
  // 용도 변경 확인(구앱 ChangeConfirmDialog) — 기존 용도와 다른 용도 선택 시.
  PLANT_USE_CHANGE_CONFIRM_TITLE: '발전소 용도 변경',
  PLANT_USE_CHANGE_CONFIRM_MESSAGE: (useCodeName: string) =>
    `발전소 용도를 '${useCodeName}'(으)로 변경합니다. 진행하시겠습니까?`,
  PLANT_USE_SUCCESS: '발전소 용도·계약 정보가 저장되었습니다.',
  PLANT_USE_ERROR: '저장에 실패했습니다.',

  // coming-soon (미구현 메뉴 placeholder)
  COMING_SOON_TITLE: '준비 중인 화면입니다.',
  COMING_SOON_DESC: '해당 메뉴는 순차적으로 제공될 예정입니다.',

  // inverter 상세분석 (/stats/inverter, 4탭) — 화면 타이틀은 NAV_STATS_INVERTER.
  // 탭1 제목·카드 지표·상태 배지는 TERM_*/STATUS_* 재사용.
  INVERTER_TAB_MPPT: 'MPPT 출력',
  INVERTER_TAB_STRING: '스트링 출력',
  INVERTER_TAB_MEASURE: '계측정보 조회',
  // 인버터 구성 탭(목록) — 발전소 없음은 공용 COMMON_NO_PLANTS_*(NoPlantsState), 발전소 선택은 컨텍스트 바(SCOPE_*).
  INVERTER_LIST_EMPTY: '인버터가 없습니다.',
  INVERTER_LIST_EMPTY_DESC: '선택한 발전소에 등록된 인버터가 없어요.',
  // 인버터 구성 카드 — 현재 출력률(대) + 지표 그리드 + 패널 한 줄.
  // 헤드라인은 **비율(%)** — 절대값(kW)은 같은 카드 지표 셀이 `DASHBOARD_METRIC_CURRENT_POWER`('현재 출력')로
  // 따로 표기하므로, 둘이 한 화면에 나란히 서는 이상 라벨로 구분돼야 한다.
  INVERTER_CARD_CURRENT_OUTPUT_RATIO: '현재 출력률',
  INVERTER_CARD_PANEL: '패널',
  // 패널구성 — 배치 그룹 1벌 "N직렬×M병렬". 여러 벌이면 카드가 **줄바꿈**해 쌓고 둘째 줄부터 `+` 를 붙인다
  // (한 줄에 몰면 그룹 경계가 안 보인다). 조립은 표시단(`inverter-card`) 책임.
  INVERTER_PANEL_MATRIX_GROUP: (serial: number, parallel: number) =>
    `${serial}직렬×${parallel}병렬`,
  // 총 장수 — 마지막 그룹 줄 끝에 1회. Σ(직렬 × 병렬).
  INVERTER_PANEL_MATRIX_TOTAL: (total: number) => `(${total}개)`,
  // 필터(탭2~4 공용)
  INVERTER_FILTER_INVERTER: '인버터 선택',
  INVERTER_FILTER_DATE: '조회일자',
  // MPPT 출력 탭 (제목·카드 없이 차트→표 — admin PlantPvOutputView 정합)
  // 표 1열(시간)·빈 상태는 TERM_TIME · COMMON_NO_RESULTS 재사용(MPPT·스트링·계측 3탭 공통).
  MPPT_COL_VOLTAGE: '전압(V)',
  MPPT_COL_CURRENT: '전류(A)',
  MPPT_COL_POWER: '전력(kW)',
  // 스트링 출력(채널별 전류 멀티라인 차트 + 표 — admin 정합, 제목·카드 없이 차트→표)
  STRING_TABLE_GROUP: '스트링별 전류값(A)', // 표 중첩 그룹 헤더(admin 정합) — 하위 = 스트링 #N
  // PV(MPPT·스트링) 차트 툴팁 — 0(미출력) 채널 처리(admin 정합).
  PV_TOOLTIP_EMPTY: '출력 없음', // 전부 결측
  PV_TOOLTIP_NO_OUTPUT: '(미출력)', // 일부 0 채널(스트링)
  PV_TOOLTIP_ALL_NO_OUTPUT: '전체 미출력', // 전부 0
  // 계측정보 필터(복수 인버터·조회내용·시간)
  INVERTER_FILTER_CONTENT: '조회내용',
  INVERTER_FILTER_CONTENT_PLANT: '발전정보',
  INVERTER_FILTER_CONTENT_ERROR: '오류정보',
  INVERTER_FILTER_START_TIME: '시작시간',
  INVERTER_FILTER_END_TIME: '종료시간',
  INVERTER_FILTER_TIME_RANGE: '조회시간', // 계측 칩 — 시각 범위
  INVERTER_HOUR: (n: string | number) => `${String(n).padStart(2, '0')}시`, // 시각 옵션·칩 공통(2자리 zero-pad: 00시~24시)
  // 계측정보 결과 — 표(제목 없음)→조회 분석 카드(admin PlantHistoryTab+PlantHistoryAnalysis 정합)
  MEASURE_ANALYSIS_TITLE: '조회 분석',
  MEASURE_ANALYSIS_INFO:
    '지표 카드를 선택하면 우측에 인버터별 상세 추이가 표시됩니다. 범례를 눌러 인버터를 켜고 끌 수 있습니다.',
  // 대표시각(3분 클러스터)과 실제 수신 시각이 다를 때 툴팁 예외 표기(admin 정합).
  MEASURE_RECEIVED_AT: (time: string) => `(${time} 수신)`,
  MEASURE_ERR_COL_TYPE: '오류구분',
  MEASURE_ERR_COL_STATUS: '상태정보',

  // 설정 화면(`/settings`) — NAV_SETTINGS 는 메뉴 라벨 겸 라우트 타이틀(단일 소스).
  NAV_SETTINGS: '설정',
  SETTINGS_SECTION_NOTIFICATIONS: '알림',
  SETTINGS_SECTION_THEME: '테마',
  SETTINGS_ALARM_PLANT_WARNING: '발전소 이상 알림',
  SETTINGS_ALARM_DAILY_REPORT: '일일리포트 (카카오 알림톡)',
  // 광고성 정보 수신 동의 — 토글 라벨 겸 동의 다이얼로그 제목. 해제 다이얼로그만 별도 문구.
  SETTINGS_MARKETING: '광고성 정보 수신 동의',
  SETTINGS_MARKETING_DISABLE_TITLE: '광고성 정보 수신 동의 해제',
  SETTINGS_MARKETING_ENABLE_MESSAGE:
    '광고성 정보(이벤트, 프로모션, 혜택 안내 등) 수신에 동의합니다. 동의를 거부하셔도 서비스 이용에는 제한이 없습니다. 동의 후에도 앱 설정에서 언제든지 동의를 철회하실 수 있습니다.',
  SETTINGS_MARKETING_DISABLE_MESSAGE:
    '광고성 정보(이벤트, 프로모션, 혜택 안내 등)를 받지 않습니다. 단, 서비스 운영에 필요한 필수 알림은 계속 발송될 수 있습니다.',
  SETTINGS_MARKETING_ENABLE_CONFIRM: '동의',
  SETTINGS_MARKETING_DISABLE_CONFIRM: '동의 해제',
  SETTINGS_MARKETING_VIEW_CONTENT: '내용 보기',
  // 알림 권한 배너(web 브라우저 전용 — native 는 앱이 권한 소유)
  SETTINGS_PERMISSION_TITLE_DEFAULT: '알림 권한이 필요해요',
  SETTINGS_PERMISSION_TITLE_BLOCKED: '알림이 차단되어 있어요',
  SETTINGS_PERMISSION_DEFAULT: '알림 권한을 허용하면 푸시 알림을 받을 수 있어요.',
  SETTINGS_PERMISSION_REQUEST: '알림 권한 요청',
  SETTINGS_PERMISSION_GRANTED: '알림 권한이 허용되었어요.',
  // denied 는 브라우저가 재프롬프트를 막으므로 리셋 방법을 안내(버튼 무의미).
  SETTINGS_PERMISSION_BLOCKED:
    '브라우저에서 알림이 차단되어 있어요. 주소창의 자물쇠(🔒) → 알림 → 허용으로 바꾼 뒤 새로고침해주세요.',
  SETTINGS_MARKETING_ENABLED: '광고성 정보 수신에 동의했습니다.',
  SETTINGS_MARKETING_DISABLED: '광고성 정보 수신 동의를 해제했습니다.',
  SETTINGS_THEME_LIGHT: '라이트',
  SETTINGS_THEME_DARK: '다크',
  SETTINGS_THEME_SYSTEM: '시스템 설정',
  SETTINGS_THEME_LIGHT_DESC: '밝은 화면',
  SETTINGS_THEME_DARK_DESC: '어두운 화면',
  SETTINGS_THEME_SYSTEM_DESC: '기기 설정에 따름',
  SETTINGS_UPDATE_FAILED: '설정 변경에 실패했습니다.',
  // 디버그 정보 — 헤더 타이틀 5연타로 여는 히든 다이얼로그(운영 빌드 포함, CS 대응용 진단값).
  SETTINGS_DEBUG_TITLE: '디버그 정보',
  SETTINGS_DEBUG_COPY: '복사',
  SETTINGS_DEBUG_COPIED: '복사됨',
  // 고객센터 — 채널톡 문의 + 약관 목록(v3 consents) + 앱 버전. 로드 실패는 AGREEMENT_ERROR.
  SUPPORT_APP_VERSION: (version: string) => `앱 버전 v${version}`,
  SUPPORT_CONSULT_HOURS: '상담시간 평일 오전 9시 ~ 오후 6시',
  SUPPORT_TERMS_SECTION: '서비스 약관 및 개인정보 동의 문서',
  SUPPORT_TERMS_REVISED: (date: string) => `최신개정 ${date}`,

  // 마이페이지(구 내 정보) — 필드·안내·변경 트리거. 이름·아이디 변경불가(구앱 verbatim).
  // 아이디·휴대폰번호·비밀번호 라벨은 TERM_*, 변경 버튼은 COMMON_CHANGE 재사용.
  MY_INFO_SECTION: '내 정보',
  MY_INFO_SUBSCRIPTION_SECTION: '발전소 구독현황',
  MY_INFO_FIELD_NAME: '이름',
  MY_INFO_FIELD_EMAIL: '이메일',
  MY_INFO_NOTICE:
    '이름, 아이디는 변경할 수 없습니다.\n관련 문의는 고객센터를 이용해 주시기 바랍니다.',
  MY_INFO_GO_SUPPORT: '고객센터 바로가기',

  // 이메일 변경 시트 — TITLE 은 시트 제목 겸 제출 버튼.
  EMAIL_CHANGE_TITLE: '이메일 변경',
  EMAIL_CHANGE_NEW: '신규 이메일',
  EMAIL_CHANGE_SUCCESS: '이메일이 변경되었습니다.',
  EMAIL_CHANGE_EMPTY: '이메일 주소를 입력해 주세요.',
  EMAIL_CHANGE_INVALID: '이메일 주소를 형식에 맞게 입력해 주세요.',
  EMAIL_CHANGE_FAILED: '이메일 변경에 실패했습니다. 다시 시도해주세요.',

  // 비밀번호 변경 시트 (2-step verify → change) — TITLE 은 시트 제목 겸 제출 버튼.
  PW_CHANGE_TITLE: '비밀번호 변경',
  PW_CHANGE_CURRENT: '현재 비밀번호',
  PW_CHANGE_NEW: '신규 비밀번호',
  PW_CHANGE_CONFIRM: '비밀번호 확인',
  PW_CHANGE_RULE:
    '신규 비밀번호를 영문 대소문자, 숫자, 특수문자 중 2개 이상의 조합으로 8자 이상 설정해주세요.',
  PW_CHANGE_SUCCESS: '비밀번호가 변경되었습니다.',
  PW_CHANGE_CURRENT_INCORRECT: '현재 비밀번호가 올바르지 않습니다.',
  PW_CHANGE_SAME_AS_CURRENT: '현재 비밀번호와 다르게 설정해주세요.',
  PW_CHANGE_RULE_VIOLATION: '비밀번호 규칙에 맞게 설정해주세요.',
  PW_CHANGE_CONFIRM_MISMATCH: '신규 비밀번호와 일치하지 않습니다.',
  PW_CHANGE_FAILED: '비밀번호 변경에 실패했습니다. 다시 시도해주세요.',

  // 휴대폰번호 변경 시트 (2-stage: 번호→인증번호 발송 / 코드→인증). 자동 채우기 제외.
  PHONE_CHANGE_TITLE: '휴대폰번호 변경',
  PHONE_CHANGE_CODE: '인증번호 입력',
  PHONE_CHANGE_SEND: '인증번호 발송',
  PHONE_CHANGE_VERIFY: '인증',
  PHONE_CHANGE_RESEND: '인증번호 재전송',
  PHONE_CHANGE_RESEND_COUNTDOWN: (seconds: number) => `인증번호 재전송 (${seconds}초)`,
  PHONE_CHANGE_SUCCESS: '휴대폰번호가 변경되었습니다.',
  PHONE_CHANGE_VERIFIED: '인증번호를 정상적으로 확인했습니다.',
  PHONE_CHANGE_TIMEOUT: '인증번호 입력시간이 초과됐습니다. 다시 시도해주세요.',
  PHONE_CHANGE_INVALID_CODE: '인증번호가 올바르지 않습니다. 다시 시도해주세요.',
  PHONE_CHANGE_SEND_FAILED: '인증번호 발송에 실패했습니다. 다시 시도해주세요.',
  PHONE_CHANGE_PHONE_REQUIRED: '휴대폰 번호를 입력해주세요.',
  PHONE_CHANGE_PHONE_INVALID: '올바른 휴대폰 번호를 입력해주세요.',

  // 발전소 정보 상세 — 구독정보 섹션(v2). 미구독 상태는 STATUS_NOT_SUBSCRIBED, 구독 CTA 는 SUBSCRIPTION_SUBSCRIBE.
  PLANT_SUBSCRIPTION_SECTION: '구독정보',
  PLANT_SUBSCRIPTION_TYPE: '구독유형',
  PLANT_SUBSCRIPTION_START: '구독시작일',
  PLANT_SUBSCRIPTION_END: '구독종료일',
  PLANT_SUBSCRIPTION_CHANGE_PAYMENT: '결제변경',
} as const;

/**
 * 구독 상태 → 표시 텍스트. **코드↔문구 매핑도 UI 텍스트의 일부**라 `UI_TEXT` 와 같은 파일에 둔다
 * (ADR domain-ssot ① — 화면 문구는 `shared/languages` 소유, entity 는 행위만).
 *
 * BE `SUBS_STATUS` 공통코드에도 라벨이 있지만(`사용 중`·`중단`·`만료`·`취소`) **문구는 우리가 소유**한다 —
 * 상태 표기는 서비스 톤을 타는 UX 카피고, 마스터 값이 바뀐다고 화면 문구가 흔들리면 곤란하다.
 * (구독 **유형**은 반대로 상품명이라 BE 마스터를 그대로 쓴다 — `COMMON_PARENT.SUBS`.)
 *
 * `Record<SubscriptionStatus, …>` 라 상태가 늘면 컴파일러가 누락을 잡는다.
 * 조회는 `codeToText(SUBSCRIPTION_STATUS_TEXT, code)` — 코드가 union 으로 안 좁혀진 자리에서도 안전.
 */
export const SUBSCRIPTION_STATUS_TEXT: Record<SubscriptionStatus, string> = {
  inactive: UI_TEXT.STATUS_NOT_SUBSCRIBED,
  active: UI_TEXT.STATUS_SUBSCRIBED,
  suspended: UI_TEXT.STATUS_SUSPENDED,
  expired: UI_TEXT.STATUS_EXPIRED,
  canceled: UI_TEXT.STATUS_CANCELED,
};
